import { api } from "../api";
import { S, isAdmin, on, render } from "../state";
import { $, $$, I, avatar, badge, closeSheet, edHead, esc, fmtDate, fmtDateTime, fullName, openSheet, sized, toast, wirePhotos } from "../ui";
import { INTERESTS, MONTHS, TITLES, formatPhone } from "../../shared/constants";
import type { StaffView } from "../../shared/types";
import { eventsTab, locationsTab, templatesTab } from "./manage";
import { cardsTab } from "./cardsadmin";
import { feedbackTab } from "./feedback";
import { pointsTab, saveCsv } from "./pointsadmin";
import { logTab, maintTab } from "./sitetools";
import { emailsTab, outboxTable, type OutboxRow } from "./emailsadmin";

const A: {
  tab: string;
  people: StaffView[] | null;
  inactive: boolean;
  q: string;
  role: string;
  loading: boolean;
  pushBad: boolean;
} = { tab: "events", people: null, inactive: false, q: "", role: "", loading: false, pushBad: false };

async function loadPeople() {
  if (A.loading) return;
  A.loading = true;
  try {
    A.people = await api<StaffView[]>(`/api/users${A.inactive ? "?inactive=1" : ""}`);
  } catch (e) {
    toast((e as Error).message);
    A.people = [];
  }
  A.loading = false;
  if (S.route === "admin") {
    const t = $("#utable");
    if (t) {
      t.innerHTML = peopleTable();
      wirePhotos(t);
    } else render();
  }
}

const roleText = (u: StaffView) =>
  [u.roles.admin && "Admin", u.roles.admissions && "Admissions", u.roles.pa && "PA", u.roles.sa && "SA"].filter(Boolean).join(", ") || "No role";

function rows() {
  const q = A.q.toLowerCase();
  return (A.people || []).filter(
    (u) =>
      (!q || `${fullName(u)} ${u.email}`.toLowerCase().includes(q)) &&
      (!A.role || u.roles[A.role as keyof StaffView["roles"]]) &&
      (!A.pushBad || ((u.roles.pa || u.roles.sa) && pushStatus(u) !== "on")),
  );
}

/** Notification status for ambassadors: On · N devices / Not set up / Needs attention. */
const pushStatus = (u: StaffView) => (u.push.failing ? "bad" : u.push.devices ? "on" : "none");
const pushChip = (u: StaffView) => {
  const st = pushStatus(u);
  if (st === "on") return `<span class="chip ok">On · ${u.push.devices} device${u.push.devices === 1 ? "" : "s"}</span>`;
  if (st === "bad") return '<span class="chip red">Needs Attention</span>';
  return '<span class="chip">Not Set Up</span>';
};

function peopleTable() {
  if (!A.people) return '<p class="muted">Loading…</p>';
  const list = rows();
  return `<p class="muted small" style="margin:0 0 8px">${list.length} ${list.length === 1 ? "person" : "people"}. Tap a row to ${isAdmin() ? "edit" : "see their profile"}.</p>
  <div class="tablewrap"><table><thead><tr><th>Name</th><th>Roles</th><th>Class of</th><th>Last Seen</th><th>Notifications</th><th>Status</th></tr></thead><tbody>${list
    .map(
      (u) => `<tr class="click" tabindex="0" data-act="open-user" data-id="${u.id}"><td><div class="row" style="gap:10px">${avatar(u.id)}<div><b>${esc(fullName(u) || "(no name yet)")}</b><div class="muted small">${esc(u.email)}</div></div></div></td>
      <td>${roleText(u)}${u.titles.length ? `<div class="muted small">${u.titles.map(esc).join(", ")}</div>` : ""}</td>
      <td>${u.roles.pa || u.roles.sa ? (u.classOf ?? "—") : "—"}</td>
      <td class="small">${u.lastSeenAt ? fmtDateTime(u.lastSeenAt) : '<span class="muted">Never signed in</span>'}</td>
      <td>${pushChip(u)}</td>
      <td><span class="chip ${u.active ? "ok" : ""}">${u.active ? "Active" : "Inactive"}</span> ${u.profileComplete ? "" : '<span class="chip warn">Profile not finished</span>'}${u.excludePoints ? ' <span class="chip">No points</span>' : ""}${u.lowActivity ? ' <span class="chip warn" title="No points all last school year">Low Activity</span>' : ""}</td></tr>`,
    )
    .join("")}</tbody></table></div>`;
}

function peopleTab() {
  if (!A.people) void loadPeople();
  // One row of filters: search, role, Show Inactive, then Add People at the end. Wraps on phones.
  return `<div class="card"><div class="row wrap" style="gap:8px 10px;margin-bottom:12px;align-items:center">
    <input type="text" placeholder="Search Name or Email" value="${esc(A.q)}" data-input="people-q" style="flex:1 1 200px;max-width:280px;width:auto" aria-label="Search">
    <select class="f" style="width:auto;flex:none" data-change="people-role" aria-label="Role"><option value="">All Roles</option>${[
      ["admin", "Admins"],
      ["admissions", "Admissions"],
      ["pa", "Parent Ambassadors"],
      ["sa", "Student Ambassadors"],
    ]
      .map(([k, l]) => `<option value="${k}" ${A.role === k ? "selected" : ""}>${l}</option>`)
      .join("")}</select>
    <label class="row small" style="gap:6px;cursor:pointer;flex:none;white-space:nowrap"><input type="checkbox" data-change="people-inactive" ${A.inactive ? "checked" : ""}> Show Inactive</label>
    <label class="row small" style="gap:6px;cursor:pointer;flex:none;white-space:nowrap"><input type="checkbox" data-change="people-pushbad" ${A.pushBad ? "checked" : ""}> Notifications Not Working</label>
    <button class="btn ghost sm" style="margin-left:auto;flex:none" data-act="export-people">Export Ambassadors</button>
    ${isAdmin() ? `<button class="btn sm" style="flex:none" data-act="add-people">${sized(I.plus, 14)} Add People</button>` : ""}</div>
    <div id="utable">${peopleTable()}</div></div>`;
}

export function adminView() {
  const tabs: [string, string][] = [
    ["events", "Events"],
    ["people", isAdmin() ? "People" : "Ambassadors"],
  ];
  if (isAdmin()) tabs.push(["templates", "Event Templates"], ["locations", "Locations"], ["points", "Points & PA of the Month"], ["cards", "Season Cards"], ["emails", "Emails"], ["feedback", "Feedback"], ["log", "Activity Log"], ["maint", "Maintenance"]);
  if (!tabs.some((t) => t[0] === A.tab)) A.tab = tabs[0][0];
  const body =
    A.tab === "events" ? eventsTab() : A.tab === "templates" ? templatesTab() : A.tab === "locations" ? locationsTab() : A.tab === "cards" ? cardsTab() : A.tab === "emails" ? emailsTab() : A.tab === "points" ? pointsTab() : A.tab === "feedback" ? feedbackTab() : A.tab === "log" ? logTab() : A.tab === "maint" ? maintTab() : peopleTab();
  return `<div class="stack"><div class="tabsrow" role="tablist">${tabs
    .map(([k, l]) => `<button class="${A.tab === k ? "on" : ""}" data-act="admin-tab" data-tab="${k}">${l}</button>`)
    .join("")}</div>${body}</div>`;
}

/* ---------------- person sheets ---------------- */

const chk = (id: string, lab: string, on: boolean, sub = "") =>
  `<label class="row" style="gap:10px;align-items:flex-start;cursor:pointer"><input type="checkbox" id="${id}" ${on ? "checked" : ""} style="margin-top:3px;width:18px;height:18px"><span>${lab}${sub ? `<div class="muted small">${sub}</div>` : ""}</span></label>`;

function titleBlock(pa: boolean, sa: boolean, on: string[]) {
  const list = [...(pa ? TITLES.pa : []), ...(sa ? TITLES.sa : [])];
  return `<h3>Titles <span class="muted small" style="text-transform:none;letter-spacing:0;font-weight:400;margin-left:6px">Shown on their badge</span></h3>${
    list.length
      ? `<div class="edchecks">${list.map((t) => `<label class="row" style="gap:10px;cursor:pointer"><input type="checkbox" class="ttl" value="${t}" ${on.includes(t) ? "checked" : ""} style="width:18px;height:18px">${t}</label>`).join("")}</div>`
      : '<p class="muted small" style="margin:0">Pick Parent or Student Ambassador above to see titles.</p>'
  }`;
}

const row = (k: string, v: string) => `<div class="row between" style="gap:12px"><span class="muted">${k}</span><span style="text-align:right">${v}</span></div>`;

/** Admissions: read-only profile. */
function viewUser(u: StaffView) {
  openSheet(`${edHead(esc(fullName(u)))}<div class="edbody">
    ${badge(u)}
    <section class="edsec"><h3>Contact</h3>${row("Email", `<a href="mailto:${esc(u.email)}">${esc(u.email)}</a>`)}${
      u.roles.pa ? row("Mobile", u.phone ? `<a href="tel:+1${u.phone.replace(/\D/g, "")}">${esc(u.phone)}</a>` : '<span class="muted">None</span>') : ""
    }</section>
    <section class="edsec"><h3>Ambassador</h3>${row("Roles", roleText(u))}${u.titles.length ? row("Titles", u.titles.map(esc).join(", ")) : ""}${
      u.roles.pa || u.roles.sa ? row("Class of", String(u.classOf ?? "—")) : ""
    }${row("Status", `<span class="chip ${u.active ? "ok" : ""}">${u.active ? "Active" : "Inactive"}</span>`)}${row("Last Seen", fmtDateTime(u.lastSeenAt))}</section>
    ${recentEmailsSec(u)}
    <p class="muted small" style="margin:0">Only admins can change someone's profile.</p></div>
    <div class="edfoot">${u.active ? `<button class="btn" data-act="send-signin" data-id="${u.id}">Send Sign-In Instructions</button>` : ""}<button class="btn ghost" data-act="close-sheet">Close</button></div>`);
  wirePhotos($("#sheet")!);
  void loadRecentEmails(u.id);
}

/** The last 5 emails to this person, so Admissions can see what they were sent. */
function recentEmailsSec(u: StaffView) {
  return `<section class="edsec"><h3>Recent Emails</h3><div id="uemails"><p class="muted small">Loading…</p></div></section>`;
}

async function loadRecentEmails(id: number) {
  try {
    const rows = await api<OutboxRow[]>(`/api/emails/user/${id}`);
    const box = $("#uemails");
    if (box) box.innerHTML = outboxTable(rows, false);
  } catch {
    const box = $("#uemails");
    if (box) box.innerHTML = '<p class="muted small">Couldn\'t load emails.</p>';
  }
}

/** Admins: edit everything. */
function editUser(u: StaffView) {
  const yrs = Array.from({ length: 21 }, (_, i) => 2020 + i);
  openSheet(`${edHead(esc(fullName(u) || u.email))}<div class="edbody">
    <section class="edsec"><h3>Contact</h3>
      <div class="grid g2"><div><label class="fld" for="uf">First Name</label><input type="text" id="uf" value="${esc(u.first)}"></div><div><label class="fld" for="ul">Last Name</label><input type="text" id="ul" value="${esc(u.last)}"></div></div>
      <div class="grid g2"><div><label class="fld" for="ue">Email</label><input type="email" id="ue" value="${esc(u.email)}"></div><div id="upwrap"><label class="fld" for="up">Mobile Phone (PAs)</label><input type="tel" id="up" value="${esc(u.phone || "")}" data-change="fmt-phone" placeholder="Optional"></div></div></section>
    <section class="edsec"><h3>Roles</h3><div class="edchecks">${chk("radmin", "Admin", u.roles.admin)}${chk("radm", "Admissions", u.roles.admissions)}${chk("rpa", "Parent Ambassador", u.roles.pa)}${chk("rsa", "Student Ambassador", u.roles.sa)}</div></section>
    <section class="edsec" id="ttlsec">${titleBlock(u.roles.pa, u.roles.sa, u.titles)}</section>
    <section class="edsec"><h3>Ambassador</h3>
      <div><label class="fld" for="ubm">Birthday</label><div class="grid" style="grid-template-columns:2fr 1fr"><select class="f" id="ubm" aria-label="Birth month"><option value="">Month</option>${MONTHS.map((m, i) => `<option value="${i + 1}" ${u.birthMonth === i + 1 ? "selected" : ""}>${m}</option>`).join("")}</select><select class="f" id="ubd" aria-label="Birth day"><option value="">Day</option>${Array.from({ length: 31 }, (_, i) => `<option ${u.birthDay === i + 1 ? "selected" : ""}>${i + 1}</option>`).join("")}</select></div></div>
      <div class="grid g2"><div><label class="fld" for="uc">Class of</label><select class="f" id="uc"><option value="">—</option>${yrs.map((y) => `<option ${y === u.classOf ? "selected" : ""}>${y}</option>`).join("")}</select><div class="muted small" style="margin-top:4px">PAs: the year their youngest current MCDS child graduates.</div></div>
      <div><label class="fld">Event Interests</label><div class="stack" style="gap:6px">${INTERESTS.map((i) => `<label class="row small" style="gap:8px;cursor:pointer"><input type="checkbox" class="uint" value="${i.key}" ${u.interests.includes(i.key) ? "checked" : ""}> ${esc(i.label)}</label>`).join("")}</div></div></div></section>
    <section class="edsec"><h3>Account</h3>
      <div class="stack" style="gap:10px">${chk("uact", "Active", u.active, "Inactive people can't sign in.")}${chk("uexc", "Exclude from Points", !!u.excludePoints, "Hidden from the points report and PA of the Month.")}${chk("unoi", "Don't Send Calendar Invites", u.noInvites, "They still get the reconfirmation email.")}</div>
      ${row("Created", fmtDate(u.createdAt))}${row("Last Seen", fmtDateTime(u.lastSeenAt))}
      <div class="row wrap" style="gap:8px">${u.photo ? `<button class="btn ghost sm" data-act="admin-del-photo" data-id="${u.id}">Delete Photo</button>` : ""}<button class="btn ghost sm" data-act="admin-signout" data-id="${u.id}">Sign Out of All Devices</button>${u.id !== S.me!.user.id ? `<button class="btn ghost sm" style="color:var(--accent)" data-act="ask-delete-user" data-id="${u.id}">Delete Person</button>` : ""}${u.active ? `<button class="btn ghost sm" data-act="send-signin" data-id="${u.id}">Send Sign-In Instructions</button>` : ""}</div></section>
    ${recentEmailsSec(u)}
    <div class="err" id="err" role="alert"></div>
  </div>
  <div class="edfoot"><button class="btn" data-act="save-user" data-id="${u.id}">Save</button><button class="btn ghost" data-act="close-sheet">Cancel</button></div>`);
  const refresh = () => {
    const on = $$<HTMLInputElement>("#sheet .ttl:checked").map((x) => x.value);
    $("#ttlsec")!.innerHTML = titleBlock(($("#rpa") as HTMLInputElement).checked, ($("#rsa") as HTMLInputElement).checked, on);
  };
  ($("#rpa") as HTMLInputElement).onchange = refresh;
  ($("#rsa") as HTMLInputElement).onchange = refresh;
  void loadRecentEmails(u.id);
}

function addRow() {
  const r = document.createElement("div");
  r.className = "addrow";
  r.innerHTML = `<input type="text" placeholder="First" aria-label="First name"><input type="text" placeholder="Last" aria-label="Last name"><input type="email" placeholder="Email" aria-label="Email"><select class="f" aria-label="Role"><option value="Admissions">Admissions</option><option value="PA" selected>PA</option><option value="SA">SA</option></select>`;
  $("#addrows")!.appendChild(r);
  return r;
}

function addPeople() {
  openSheet(`${edHead("Add People")}<div class="edbody">
    <section class="edsec"><h3>People</h3><p class="muted small" style="margin:-4px 0 0">One person per row.</p>
      <div id="addrows" class="stack" style="gap:8px"></div>
      <div><button class="btn ghost sm" data-act="add-row">${sized(I.plus, 14)} Another Row</button></div></section>
    <div class="edtpl"><label class="fld" for="paste">Or Paste from a Spreadsheet (First, Last, Email, Role)</label><textarea id="paste" placeholder="Jane, Silver, jane@example.com, PA" data-input="paste-people"></textarea></div>
    <div class="edtpl small">No email goes out when you add people. They can sign in any time with the email you enter here.</div>
    <div class="err" id="err" role="alert"></div></div>
    <div class="edfoot"><button class="btn" data-act="save-people">Add People</button><button class="btn ghost" data-act="close-sheet">Cancel</button></div>`);
  addRow();
  addRow();
  addRow();
}

const findUser = (id: number) => A.people?.find((u) => u.id === id);

on({
  "admin-tab": (el) => {
    A.tab = el.dataset.tab!;
    render();
  },
  "people-q": (el) => {
    A.q = (el as HTMLInputElement).value;
    $("#utable")!.innerHTML = peopleTable();
    wirePhotos($("#utable")!);
  },
  "people-role": (el) => {
    A.role = (el as HTMLSelectElement).value;
    $("#utable")!.innerHTML = peopleTable();
    wirePhotos($("#utable")!);
  },
  "people-inactive": (el) => {
    A.inactive = (el as HTMLInputElement).checked;
    void loadPeople();
  },
  "people-pushbad": (el) => {
    A.pushBad = (el as HTMLInputElement).checked;
    $("#utable")!.innerHTML = peopleTable();
    wirePhotos($("#utable")!);
  },
  "open-user": (el) => {
    const u = findUser(Number(el.dataset.id));
    if (!u) return;
    if (isAdmin()) editUser(u);
    else viewUser(u);
  },
  "add-people": () => addPeople(),
  "ask-delete-user": (el) => {
    const u = findUser(Number(el.dataset.id));
    if (!u) return;
    openSheet(`${edHead(`Delete ${esc(fullName(u) || u.email)}?`)}<div class="edbody">
      <p style="margin:0">This can't be undone. Their profile, photo and devices are deleted, and they're taken off upcoming events. Past events keep their name as "${esc(fullName(u))} (deleted)".</p>
      <p class="muted small" style="margin:0">To keep their history and just stop them signing in, turn off Active instead.</p>
      <div><label class="fld" for="delconf">Type DELETE to Confirm</label><input type="text" id="delconf" autocomplete="off"></div>
      <div class="err" id="err" role="alert"></div></div>
      <div class="edfoot"><button class="btn danger" data-act="delete-user" data-id="${u.id}">Delete Permanently</button><button class="btn ghost" data-act="close-sheet">Cancel</button></div>`);
  },
  "delete-user": async (el) => {
    try {
      await api(`/api/users/${el.dataset.id}`, { method: "DELETE", body: { confirm: ($("#delconf") as HTMLInputElement).value } });
      closeSheet();
      toast("Deleted.");
      A.people = null;
      render();
    } catch (e) {
      $("#err")!.textContent = (e as Error).message;
    }
  },
  "export-people": async () => {
    // Current ambassadors, or everyone when Show Inactive is on.
    try {
      const rows = await api<any[]>(`/api/users/export${A.inactive ? "?all=1" : ""}`);
      saveCsv(`ambassadors${A.inactive ? "-all" : ""}.csv`, [
        ["First", "Last", "Email", "Role", "Phone", "Class of", "Profile Created", "Points This School Year", "Lifetime Points", "Active", "Low Activity"],
        ...rows.map((r) => [r.first, r.last, r.email, r.role, r.phone || "", r.classOf ?? "", new Date(r.createdAt).toLocaleDateString("en-US"), r.pointsThisYear, r.lifetimePoints, r.active ? "Yes" : "No", r.lowActivity ? "Yes" : ""]),
      ]);
    } catch (e) {
      toast((e as Error).message);
    }
  },
  "add-row": () => void addRow(),
  "paste-people": () => {
    const ta = $("#paste") as HTMLTextAreaElement;
    const lines = ta.value
      .split(/\r?\n/)
      .map((l) => l.split(/\t|,/).map((x) => x.trim()))
      .filter((l) => l.length >= 3 && l[2].includes("@"));
    if (!lines.length) return;
    $("#addrows")!.innerHTML = "";
    for (const l of lines) {
      const r = addRow();
      const ins = r.querySelectorAll("input");
      ins[0].value = l[0];
      ins[1].value = l[1];
      ins[2].value = l[2];
      const ro = (l[3] || "").toLowerCase();
      r.querySelector("select")!.value = ro.startsWith("s") ? "SA" : ro.startsWith("a") ? "Admissions" : "PA";
    }
    ta.value = "";
    toast(`${lines.length} rows filled in. Check them, then add.`);
  },
  "save-people": async () => {
    const people = $$("#addrows .addrow")
      .map((r) => {
        const i = r.querySelectorAll("input");
        return { first: i[0].value.trim(), last: i[1].value.trim(), email: i[2].value.trim(), role: r.querySelector("select")!.value };
      })
      .filter((r) => r.first || r.last || r.email);
    const err = (m: string) => (($("#err") as HTMLElement).textContent = m);
    if (!people.length) return err("Add at least one person.");
    if (people.some((p) => !p.first || !p.last || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(p.email))) return err("Each row needs a first name, last name and a valid email.");
    try {
      const r = await api<{ created: number[]; errors: { row: number; error: string }[] }>("/api/users", { body: { people } });
      if (r.errors.length) {
        err(r.errors.map((e) => e.error).join(" "));
        if (r.created.length) toast(`${r.created.length} added`);
        void loadPeople();
        return;
      }
      closeSheet();
      toast(`${r.created.length} added. No emails were sent.`);
      void loadPeople();
    } catch (e) {
      err((e as Error).message);
    }
  },
  "save-user": async (el) => {
    const id = Number(el.dataset.id);
    const v = (k: string) => ($("#" + k) as HTMLInputElement).value.trim();
    const c = (k: string) => ($("#" + k) as HTMLInputElement).checked;
    const err = (m: string) => (($("#err") as HTMLElement).textContent = m);
    if (!c("rpa") && !c("rsa") && !c("radm")) return err("Everyone needs at least one role: Admissions, PA or SA.");
    const phone = v("up");
    if (phone && c("rpa") && !formatPhone(phone)) return err("Enter a 10-digit phone number.");
    const body = {
      first: v("uf"),
      last: v("ul"),
      email: v("ue"),
      phone: c("rpa") ? phone : "",
      roles: { admin: c("radmin"), admissions: c("radm"), pa: c("rpa"), sa: c("rsa") },
      titles: $$<HTMLInputElement>("#sheet .ttl:checked").map((x) => x.value),
      birthMonth: v("ubm") || null,
      birthDay: v("ubd") || null,
      classOf: v("uc") || null,
      interests: $$<HTMLInputElement>("#sheet .uint:checked").map((x) => x.value),
      active: c("uact"),
      excludePoints: c("uexc"),
      noInvites: c("unoi"),
    };
    try {
      await api(`/api/users/${id}`, { method: "PATCH", body });
      closeSheet();
      toast("Saved");
      void loadPeople();
    } catch (e) {
      err((e as Error).message);
    }
  },
  "send-signin": async (el) => {
    try {
      await api(`/api/users/${el.dataset.id}/send-signin`, { method: "POST" });
      toast(S.me?.launched ? "Sign-in instructions sent" : "Recorded. Before launch, emails only go to you.");
    } catch (e) {
      toast((e as Error).message);
    }
  },
  "admin-del-photo": async (el) => {
    try {
      await api(`/api/users/${el.dataset.id}/photo`, { method: "DELETE" });
      toast("Photo deleted");
      closeSheet();
      void loadPeople();
    } catch (e) {
      toast((e as Error).message);
    }
  },
  "admin-signout": async (el) => {
    try {
      await api(`/api/users/${el.dataset.id}/signout-all`, { method: "POST" });
      toast("Signed out of all devices");
    } catch (e) {
      toast((e as Error).message);
    }
  },
});

/** View As changes what this list may show, so reload it next time. */
export function resetAdminCache() {
  A.people = null;
}
